import {Injectable} from '@angular/core';
import {CallLogEntryModel, SMSLogEntryModel} from '@app/models/call-log-entry.model';
import {AbstractContactModel} from '@app/models/abstract-contact.model';
import {checkResponse} from '@app/shared/utils/app-utils';
import {AttendantService, GetActiveCallsResponse, Response} from '@gt-api';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {CallCause} from "@app/shared/utils/call-cause";
import {getLocaleWeekEndRange} from "@angular/common";
import {GtPhoneNumberUtil} from "@app/shared/utils/gt-phone-number.util";

@Injectable({
  providedIn: 'root'
})
export class GtAttendantService {
  constructor(private attendantService: AttendantService) {}

  getActiveCalls(organization?: string, login?: string): Observable<GetActiveCallsResponse> {
    return this.attendantService.getActiveCalls1(organization, login);
  }

  getAgentSMSLog(organization?: string, login?: string, from?: string, to?: string): Observable<SMSLogEntryModel[]> {
    return new Observable(obs => {
      this.attendantService.getAgentSMSLog1(organization, login, from, to).subscribe(
        res => {
          if (res.code === 200) {
            obs.next(res.agentSmsLog.map(SMSLogEntryModel.fromSMSLogEntry));
            obs.complete();
          } else {
            obs.error();
          }
        },
        () => obs.error()
      );
    });
  }

  getAgentCallLog(
    organization?: string,
    login?: string,
    from?: string,
    to?: string,
    contacts: AbstractContactModel[] = []
  ): Observable<CallLogEntryModel[]> {
    console.log('from=' + from + ' ' + 'to=' + to);
    return new Observable(obs => {
      this.attendantService.getAgentCallLog1(organization, login, from, to).subscribe(
        res => {
          if (res.code === 200) {
            obs.next(
              res.callLog.map(entry => {
                const answeredContact = contacts.find(e => {
                  return e.login === entry.login;
                });
                const answeredByName = answeredContact ? `${answeredContact.name} (${GtPhoneNumberUtil.toDisplay(answeredContact.phoneNumber)})` : '';
                return CallLogEntryModel.convertCallLogEntry(entry, answeredByName);
              })
            );
            obs.complete();
          } else {
            obs.error();
          }
        },
        () => obs.error()
      );
    });
  }

  getServiceNumberCallLog(
    serviceNumber: string,
    organization?: string,
    login?: string,
    from?: string,
    to?: string,
    contacts: AbstractContactModel[] = []
  ): Observable<CallLogEntryModel[]> {
    return this.attendantService.getCallLog1(serviceNumber, organization, login, from, to).pipe(
      checkResponse(),
      map(res => {
        return res.callLog.map(entry => {
          const matches = contacts.reduce(
            (acc, contact) => {
              // if login is not set, use last entry in history to determine who answered
              if (entry.login === null && entry.cause === CallCause.Answered && entry.history) {
                const history = entry.history.split(',');
                const lastAnswer = history.reverse().find(h => h.includes(':100'));
                const lastAnswerNumber = lastAnswer?.substring(0, lastAnswer.indexOf(':'));
                if (lastAnswerNumber) {
                  if (lastAnswerNumber.includes(contact.login)) {
                    acc.answeredBy = `${contact.name} (${GtPhoneNumberUtil.toDisplay(lastAnswerNumber)})`;
                  } else {
                    acc.answeredBy = GtPhoneNumberUtil.toDisplay(lastAnswerNumber);
                  }
                }
              }
              if (contact.login === entry.login) {
                acc.answeredBy = `${contact.name} (${GtPhoneNumberUtil.toDisplay(contact.phoneNumber)})`;
              }
              if (contact.login === entry.handledBy) {
                acc.handledBy = `${contact.name} (${GtPhoneNumberUtil.toDisplay(contact.phoneNumber)})`;
              }
              return acc;
            },
            {
              answeredBy: '',
              handledBy: ''
            }
          );
          return CallLogEntryModel.convertCallLogEntry(entry, matches.answeredBy, matches.handledBy);
        });
      })
    );
  }

  click2Call(callee: string, displayNumber: string): Observable<Response> {
    return this.attendantService.clickToCall1(callee, displayNumber);
  }

  transferCall(sessionId: number, transferee: string, consult: boolean): Observable<Response> {
    return this.attendantService.transferCall1(sessionId, transferee, consult ? 3 : 0);
  }

  disconnectCall(sessionId: number, leg: string): Observable<Response> {
    return this.attendantService.disconnectCall1(sessionId, leg);
  }

  parkCall(sessionId: number): Observable<Response> {
    return this.attendantService.parkCall1(sessionId);
  }

  dequeueCallToColleague(sessionId: number, login: string): Observable<Response> {
    return this.attendantService.dequeueCallToColleague1(sessionId, login);
  }

  completeTransferCall(sessionId: number): Observable<Response> {
    return this.attendantService.completeTransferCall1(sessionId);
  }

  toggleCall(sessionId: number, leg: string): Observable<Response> {
    return this.attendantService.toggleCall1(sessionId, leg);
  }

  setCallLogHandled(organization: string, login: string, id: number): Observable<Response> {
    return this.attendantService.setCallLogHandled1(id, organization, login, login);
  }

  retrieveCall(sessionId: number, intendedLeg: string): Observable<Response> {
    return this.attendantService.retrieveCall1(sessionId, intendedLeg);
  }
}
